/**
 * usePwaInstallPrompt.js
 * ─────────────────────────────────────────────────────────────────────────────
 * PWA INSTALL PROMPT — Fuente de verdad única para el evento beforeinstallprompt.
 *
 * PROBLEMA RESUELTO:
 *   Chrome lanza "The prompt() method must be called with a user gesture" cuando:
 *     1) Se llama a .prompt() fuera de un click handler, O
 *     2) Múltiples listeners capturan el mismo evento y uno lo "consume".
 *
 * SOLUCIÓN: El evento se captura UNA SOLA VEZ a nivel de módulo (singleton).
 *   Todos los componentes que necesiten el install prompt usan este hook,
 *   compartiendo la misma referencia. Así .prompt() solo se llama una vez.
 *
 * USO:
 *   const { canInstallPwa, showInstallPrompt, isInstalled } = usePwaInstallPrompt();
 *
 *   // Mostrar botón solo cuando canInstallPwa === true
 *   // Llamar showInstallPrompt() únicamente desde un onClick de usuario
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useState, useEffect, useCallback } from "react";

// ── Singleton a nivel de módulo ───────────────────────────────────────────────
// El evento se captura una sola vez para toda la vida de la página.
// React remonta/desmonta componentes, pero este módulo persiste.
let _deferredPrompt = null;
let _isInstalled =
  typeof window !== "undefined" &&
  window.matchMedia("(display-mode: standalone)").matches;

// Set de callbacks que los hooks activos escuchan para actualizaciones
const _listeners = new Set();

function _notifyListeners() {
  _listeners.forEach((fn) => fn());
}

// Registrar listeners globales una sola vez (fuera de React)
if (typeof window !== "undefined") {
  // ── beforeinstallprompt: Chrome/Edge/Android ──────────────────────────────
  window.addEventListener("beforeinstallprompt", (e) => {
    // CRÍTICO: preventDefault evita que Chrome muestre su mini-infobar nativa.
    // Nosotros mostramos nuestro propio UI y llamamos .prompt() desde un click.
    e.preventDefault();
    _deferredPrompt = e;
    _notifyListeners();
  });

  // ── appinstalled: cuando el usuario acepta la instalación ─────────────────
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _isInstalled = true;
    _notifyListeners();
  });
}

// ── Hook público ──────────────────────────────────────────────────────────────
export function usePwaInstallPrompt() {
  // Refleja el estado del singleton en React
  const [canInstallPwa, setCanInstallPwa] = useState(!!_deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(_isInstalled);

  useEffect(() => {
    // Suscribirse a cambios del singleton
    const onUpdate = () => {
      setCanInstallPwa(!!_deferredPrompt);
      setIsInstalled(_isInstalled);
    };
    _listeners.add(onUpdate);
    // Sincronizar estado actual por si el evento ya se recibió antes del mount
    onUpdate();
    return () => _listeners.delete(onUpdate);
  }, []);

  /**
   * showInstallPrompt()
   * ─────────────────────────────────────────────────────────────────────────
   * DEBE llamarse ÚNICAMENTE desde un manejador de click de usuario.
   * Llamarla en useEffect o en código asíncrono sin gesto de usuario
   * provocará el aviso de Chrome:
   *   "The prompt() method must be called with a user gesture"
   */
  const showInstallPrompt = useCallback(async () => {
    if (!_deferredPrompt) return { outcome: "dismissed" };

    // Llamar al prompt nativo del navegador (solo válido con gesto de usuario)
    _deferredPrompt.prompt();
    const { outcome } = await _deferredPrompt.userChoice;

    // Limpiar referencia — el evento ya fue consumido
    _deferredPrompt = null;
    _isInstalled = outcome === "accepted";
    _notifyListeners();

    return { outcome };
  }, []);

  return { canInstallPwa, showInstallPrompt, isInstalled };
}
